import React, { useState, useEffect, useRef, useCallback } from 'react'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'components/Luxkit/Button/IconButton'
import SolidMessageIcon from 'components/Luxkit/Icons/solid/SolidMessageIcon'
import { useIsMobileScreen } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import { connect } from 'react-redux'

interface MappedStateProps {
  currentChat: App.Chat | undefined;
  outageMessage: string | undefined;
}
interface Props {
  isTyping: boolean;
}

const validateInput = (message: string, actionValidation?: { regex: string, message: string}) => {
  if (!actionValidation) {
    return message.length < 250 ? '' : 'Messages are limited to 250 characters'
  }

  return new RegExp(actionValidation.regex).test(message) ? '' : actionValidation.message
}

function MessageInput(props: Props & MappedStateProps) {
  const { currentChat, outageMessage, isTyping } = props

  const dispatch = useAppDispatch()

  const messages: Array<App.ChatMessage> = useAppSelector(state => state.supportAssistant.messages[currentChat?.id ?? 'default'] ?? EmptyArray)
  const [message, setMessage] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobileScreen()

  const lastMessage = last(messages)
  const isLastMessageByUser = lastMessage?.sender === 'user'
  const lastMessageMetaInputAction = lastMessage?.meta?.content?.find((meta) => meta.type === 'input')

  const validationMessage = validateInput(message, lastMessageMetaInputAction?.validation)
  const isMessageValid = validationMessage === ''

  const isInputButtonDisabled = !lastMessageMetaInputAction && (currentChat?.status === 'CLOSED' || !message || isTyping || !!outageMessage || (currentChat?.agentType === 'BOT' && isLastMessageByUser)) || !isMessageValid
  const isInputDisabled = currentChat?.status === 'CLOSED'

  useEffect(() => {
    if (!message && !isMobile) {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }
  }, [isMobile, message])

  const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value)
  }

  const handleMessageSubmit = useCallback(() => {
    // we need to attach the action of the last message to the payload.
    // This is so the back end understands the action to be done when free text is entered.
    const messageMeta = lastMessageMetaInputAction ?
        {
          action: lastMessageMetaInputAction.action?.key,
          payload: { ...lastMessage?.meta?.payload, inputPayload: message },
        } : {
          payload: lastMessage?.meta?.payload,
        }

    dispatch(addChatMessage(message, messageMeta))
    setMessage('')
  }, [dispatch, lastMessage?.meta?.payload, lastMessageMetaInputAction, message])

  const onKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = event.type === 'keyup' && event.key === 'Enter'

    if (isEnter && !isInputButtonDisabled) {
      handleMessageSubmit()
    }
  }

  const onMessageSubmit = () => {
    handleMessageSubmit()
  }

  const placeholder = lastMessageMetaInputAction ? lastMessageMetaInputAction?.text : 'Type a message'

  return (
    <TextInput
      type="text"
      maxLength={250}
      disabled={isInputDisabled}
      name="Message"
      aria-label="Message"
      placeholder={placeholder}
      onChange={onMessageChange}
      value={message}
      ref={inputRef}
      onKeyUp={onKeyEnter}
      invalidErrorMessage={outageMessage}
      manualError
      noValidationSpacing
      helpText={validationMessage}
      endIcon={<IconButton
        kind={lastMessageMetaInputAction ? 'primary' : 'tertiary'}
        variant="dark"
        shape="circle"
        outdent="right"
        onClick={onMessageSubmit}
        disabled={isInputButtonDisabled}
      >
        <SolidMessageIcon />
      </IconButton>}
    />
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    currentChat: selectCurrentChat(state),
    outageMessage: selectOutageMessage(state),
  }
})(MessageInput)
